/**
 * HOW a lane-v2 ask is answered — by `respond`, by a chat TURN, or by BOTH (`respond`, then a turn
 * that continues deterministically).
 *
 * A pre-task ask (`task_id === null`) of an INFORM kind is recorded by `POST …/respond`, but what
 * resumes on it depends on what the server advertises (`GET /api/sessions/capabilities` →
 * `turn_answers_ask`):
 *
 *   · capability ABSENT (older server) — `POST …/respond` records the answer but nothing resumes on
 *     it (a known MSA seam, fixed separately). What DOES continue is a chat turn: MSA's `bind_reply`
 *     binds a reply to the open ask (its own LLM enum) and carries on. Route: `turn` — the card's
 *     answer is submitted as a turn whose text is the chosen option's LABEL or the typed free text.
 *   · capability PRESENT — `POST …/respond` records the answer AND (when it returns
 *     `applied_to: {type: "turn_required"}`) a turn with `answers_ask_id` set continues that exact
 *     ask deterministically, no LLM binding. Route: `respond_then_turn`.
 *
 * Either way the turn text is sent verbatim, never interpreted here (CLAUDE.md rule 22: the client
 * decides the ROUTE from machine facts — `task_id`, `kind`, the server's advertised capability — and
 * never reads meaning out of what the user typed).
 *
 * Every ask with a `task_id` is answered with `respond` (the task owns it and resumes on it).
 */
import type { Interaction } from "../protocol";
import type { AskView } from "./types";

/** The inform kinds (MSA `dialogue` purpose=inform). Authorize kinds are never answered by a turn. */
export const INFORM_ASK_KINDS: ReadonlySet<string> = new Set(["clarify_scope", "choose_substitute", "provide_input"]);

export type AskAnswerRoute = "respond" | "turn" | "respond_then_turn";

/** The machine facts `answerRoute` needs from the server's advertised capabilities. */
export interface AskRouteCapabilities {
  /** R0 `turn_answers_ask` — see the module doc. */
  turnAnswersAsk: boolean;
}

export function answerRoute(
  ask: Pick<AskView, "task_id"> & { interaction: Pick<Interaction, "kind"> },
  capabilities: AskRouteCapabilities,
): AskAnswerRoute {
  if (ask.task_id !== null || !INFORM_ASK_KINDS.has(ask.interaction.kind)) return "respond";
  return capabilities.turnAnswersAsk ? "respond_then_turn" : "turn";
}

/**
 * The turn text for an answer given on a turn-routed ask: the typed text when there is any (it is
 * the more specific answer), else the chosen option's label, else `null` (nothing to send). The
 * option label is the human-readable choice the user saw — sending its id would ask the binder to
 * decode our vocabulary.
 */
export function turnTextForAnswer(
  interaction: Pick<Interaction, "options">,
  answer: { option_id?: string | null; free_text?: string | null },
): string | null {
  const typed = answer.free_text?.trim();
  if (typed) return typed;
  const option = answer.option_id ? interaction.options.find((o) => o.option_id === answer.option_id) : undefined;
  const label = option?.label?.trim();
  return label || null;
}
